import { useState } from 'react';
import { useDAWStore } from '../../store/daw-store';
import type { NoteEvent } from '../../store/daw-store';

type GridSize = '1/4' | '1/8' | '1/16' | '1/32' | '1/4T' | '1/8T' | '1/16T';

interface QuantizeSettings {
  gridSize: GridSize;
  strength: number; // 0-100%
  swing: number; // 0-100%
  humanize: boolean;
  humanizeAmount: number; // 0-100%
}

export const QuantizeEditor = () => {
  const { pianoRollNotes, updateNote } = useDAWStore();
  
  const [settings, setSettings] = useState<QuantizeSettings>({
    gridSize: '1/16',
    strength: 100,
    swing: 0,
    humanize: false,
    humanizeAmount: 10,
  });
  
  const [originalNotes, setOriginalNotes] = useState<NoteEvent[]>([]);
  const [previewMode, setPreviewMode] = useState(false);
  
  // Convert grid size to beats
  const gridSizeToBeats = (gridSize: GridSize): number => {
    const map: Record<GridSize, number> = {
      '1/4': 1,
      '1/8': 0.5,
      '1/16': 0.25,
      '1/32': 0.125,
      '1/4T': 2/3, // Triplet
      '1/8T': 1/3,
      '1/16T': 1/6,
    };
    return map[gridSize];
  };
  
  // Apply swing to a time value
  const applySwing = (time: number, gridSize: number, swingAmount: number): number => {
    if (swingAmount === 0) return time;
    
    // Swing affects every other grid position
    const gridPosition = Math.round(time / gridSize);
    const isOffBeat = gridPosition % 2 === 1;
    
    if (isOffBeat) {
      // Delay off-beat notes by swing amount
      const swingOffset = (gridSize * swingAmount) / 100;
      return time + swingOffset;
    }
    
    return time;
  };
  
  // Add humanize variations
  const applyHumanize = (time: number, velocity: number, humanizeAmount: number): { time: number; velocity: number } => {
    if (humanizeAmount === 0) return { time, velocity };
    
    // Random timing variation (±humanizeAmount% of a 1/32 note)
    const timingVariation = (0.125 * humanizeAmount / 100) * (Math.random() * 2 - 1);
    
    // Random velocity variation (±humanizeAmount% of current velocity)
    const velocityVariation = (velocity * humanizeAmount / 100) * (Math.random() * 2 - 1);
    
    return {
      time: time + timingVariation,
      velocity: Math.max(0, Math.min(1, velocity + velocityVariation)),
    };
  };
  
  // Quantize notes based on settings
  const quantizeNotes = (notes: NoteEvent[], settings: QuantizeSettings, preview: boolean = false): NoteEvent[] => {
    const gridSize = gridSizeToBeats(settings.gridSize);
    const strengthFactor = settings.strength / 100;
    
    return notes.map(note => {
      // Find nearest grid position
      const nearestGrid = Math.round(note.time / gridSize) * gridSize;
      
      // Move note toward grid based on strength
      let quantizedTime = note.time + (nearestGrid - note.time) * strengthFactor;
      
      // Apply swing
      quantizedTime = applySwing(quantizedTime, gridSize, settings.swing);
      
      // Apply humanize
      let finalVelocity = note.velocity;
      if (settings.humanize) {
        const humanized = applyHumanize(quantizedTime, note.velocity, settings.humanizeAmount);
        quantizedTime = humanized.time;
        finalVelocity = humanized.velocity;
      }
      
      // If preview mode, don't actually update the store
      if (preview) {
        return {
          ...note,
          time: quantizedTime,
          velocity: finalVelocity,
        };
      }
      
      return {
        ...note,
        time: quantizedTime,
        velocity: finalVelocity,
      };
    });
  };
  
  // Handle apply quantization
  const handleApply = () => {
    // Save original notes for undo
    setOriginalNotes([...pianoRollNotes]);
    
    // Apply quantization
    const quantized = quantizeNotes(pianoRollNotes, settings, false);
    quantized.forEach(note => {
      updateNote(note.id, { time: note.time, velocity: note.velocity });
    });
    
    setPreviewMode(false);
  };
  
  // Handle preview
  const handlePreview = () => {
    if (!previewMode) {
      setOriginalNotes([...pianoRollNotes]);
      setPreviewMode(true);
      
      // Apply quantization as preview
      const quantized = quantizeNotes(pianoRollNotes, settings, false);
      quantized.forEach(note => {
        updateNote(note.id, { time: note.time, velocity: note.velocity });
      });
    }
  };
  
  // Handle undo
  const handleUndo = () => {
    if (originalNotes.length > 0) {
      originalNotes.forEach(note => {
        updateNote(note.id, { time: note.time, velocity: note.velocity });
      });
      setOriginalNotes([]);
      setPreviewMode(false);
    }
  };
  
  return (
    <div className="bg-zinc-800 rounded-lg p-6 border border-zinc-700">
      <h3 className="text-lg font-semibold text-white mb-4">Advanced Quantization</h3>
      
      {/* Grid Size Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-zinc-300 mb-2">
          Grid Size
        </label>
        <div className="grid grid-cols-7 gap-2">
          {(['1/4', '1/8', '1/16', '1/32', '1/4T', '1/8T', '1/16T'] as GridSize[]).map((size) => (
            <button
              key={size}
              onClick={() => setSettings({ ...settings, gridSize: size })}
              className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                settings.gridSize === size
                  ? 'bg-indigo-600 text-white'
                  : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
              }`}
            >
              {size}
            </button>
          ))}
        </div>
      </div>
      
      {/* Strength Slider */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-zinc-300 mb-2">
          Strength: {settings.strength}%
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={settings.strength}
          onChange={(e) => setSettings({ ...settings, strength: parseInt(e.target.value) })}
          className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
        />
        <div className="flex justify-between text-xs text-zinc-500 mt-1">
          <span>0%</span>
          <span>50%</span>
          <span>100%</span>
        </div>
      </div>
      
      {/* Swing Slider */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-zinc-300 mb-2">
          Swing: {settings.swing}%
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={settings.swing}
          onChange={(e) => setSettings({ ...settings, swing: parseInt(e.target.value) })}
          className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
        />
        <div className="flex justify-between text-xs text-zinc-500 mt-1">
          <span>None</span>
          <span>Medium</span>
          <span>Heavy</span>
        </div>
      </div>
      
      {/* Humanize Toggle */}
      <div className="mb-6">
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.humanize}
            onChange={(e) => setSettings({ ...settings, humanize: e.target.checked })}
            className="w-5 h-5 rounded bg-zinc-700 border-zinc-600 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-zinc-900"
          />
          <span className="text-sm font-medium text-zinc-300">Enable Humanize</span>
        </label>
        
        {settings.humanize && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Humanize Amount: {settings.humanizeAmount}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={settings.humanizeAmount}
              onChange={(e) => setSettings({ ...settings, humanizeAmount: parseInt(e.target.value) })}
              className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
            <div className="flex justify-between text-xs text-zinc-500 mt-1">
              <span>Subtle</span>
              <span>Moderate</span>
              <span>Extreme</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Preview Section */}
      {previewMode && (
        <div className="mb-6 p-4 bg-zinc-900 rounded-lg border border-indigo-600">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-indigo-400">Preview Mode Active</span>
            <span className="text-xs text-zinc-400">{pianoRollNotes.length} notes affected</span>
          </div>
          <p className="text-xs text-zinc-400">
            Notes are temporarily quantized. Click "Apply" to confirm or "Undo" to revert.
          </p>
        </div>
      )}
      
      {/* Action Buttons */}
      <div className="flex space-x-3">
        <button
          onClick={handlePreview}
          disabled={pianoRollNotes.length === 0 || previewMode}
          className="flex-1 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 disabled:bg-zinc-800 disabled:text-zinc-600 text-white rounded-lg font-medium transition-colors"
        >
          Preview
        </button>
        <button
          onClick={handleApply}
          disabled={pianoRollNotes.length === 0}
          className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-zinc-800 disabled:text-zinc-600 text-white rounded-lg font-medium transition-colors"
        >
          Apply
        </button>
        <button
          onClick={handleUndo}
          disabled={originalNotes.length === 0}
          className="flex-1 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 disabled:bg-zinc-800 disabled:text-zinc-600 text-white rounded-lg font-medium transition-colors"
        >
          Undo
        </button>
      </div>
      
      {/* Info */}
      <div className="mt-4 p-3 bg-zinc-900 rounded border border-zinc-700">
        <p className="text-xs text-zinc-400">
          <strong className="text-zinc-300">Tip:</strong> Use lower strength values (30-70%) to preserve musical feel while correcting timing.
          Humanize adds subtle variations to prevent robotic sound.
        </p>
      </div>
    </div>
  );
};
